import { useCallback, useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  MenuBook as JournalIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import { PDFViewer } from "@react-pdf/renderer";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import { PurchaseInvoicePdf, StatusEnum } from "@/features/purchase-invoices";
import {
  DownPaymentAllocationsSection,
  ExpensesSection,
  GeneralInformationSection,
  ProductVariantsSection,
} from "@/features/purchase-invoices/purchase-invoice";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import {
  CenteredLoading,
  JournalDetailsDialog,
  PageContainer,
  ResponsiveToolbarButton,
  TabLabelWithCount,
} from "@/features/ui";

export const PurchaseInvoice = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const { outlet, user, hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [purchaseInvoice, setPurchaseInvoice] = useState(null);
  const [error, setError] = useState(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [journalDialogOpen, setJournalDialogOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const loadPurchaseInvoice = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    setError(null);
    try {
      const {
        data: { data },
      } = await apiOutletClient.get(`purchase-invoices/${referenceNumber}`, {
        params: {
          withCreator: true,
          withBuyer: true,
          withSeller: true,
          withSellerBankAccount: true,
          withPaymentTerm: true,
          withProductVariants: true,
          withTransactionHistories: true,
          withPurchaseInvoiceExpenses: true,
          withPurchaseDownPaymentAllocations: true,
        },
      });
      setPurchaseInvoice(data);
    } catch (error) {
      setError(
        getErrorMessage(error, "Gagal memuat faktur pembelian")
      );
    } finally {
      setLoading(false);
    }
  }, [outlet, referenceNumber]);

  useEffect(() => {
    loadPurchaseInvoice();
  }, [loadPurchaseInvoice]);

  if (loading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  const toolbarContent = (
    <>
      {hasPermission(PermissionEnum.UPDATE_PURCHASE_INVOICES) &&
        purchaseInvoice.status === StatusEnum.UNPAID && (
          <ResponsiveToolbarButton
            label="Ubah"
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate("edit")}
          />
        )}
      <ResponsiveToolbarButton
        label="Rincian Jurnal"
        variant="outlined"
        startIcon={<JournalIcon />}
        onClick={() => setJournalDialogOpen(true)}
      />
      <ResponsiveToolbarButton
        label="Cetak PDF"
        variant="outlined"
        startIcon={<PdfIcon />}
        onClick={() => setPdfDialogOpen(true)}
      />
    </>
  );

  return (
    <PageContainer
      maxWidth="xl"
      title="Faktur Pembelian"
      breadcrumbs={[
        { title: "Faktur Pembelian", path: "/procurement/purchase-invoices" },
        { title: referenceNumber },
      ]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <Stack spacing={2}>
        <GeneralInformationSection purchaseInvoice={purchaseInvoice} />

        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Tabs
            value={tabIndex}
            onChange={(_, newValue) => setTabIndex(newValue)}
          >
            <Tab
              label={
                <TabLabelWithCount
                  label="Faktur"
                  count={
                    purchaseInvoice.purchaseInvoiceProductVariants?.length ?? 0
                  }
                />
              }
            />
            <Tab
              label={
                <TabLabelWithCount
                  label="Biaya Lain-lain"
                  count={purchaseInvoice.purchaseInvoiceExpenses?.length ?? 0}
                />
              }
            />
            <Tab
              label={
                <TabLabelWithCount
                  label="Uang Muka"
                  count={
                    purchaseInvoice.purchaseDownPaymentAllocations?.length ?? 0
                  }
                />
              }
            />
          </Tabs>
          <Divider sx={{ mb: 2.5 }} />

          {tabIndex === 0 && (
            <ProductVariantsSection
              purchaseInvoiceProductVariants={
                purchaseInvoice.purchaseInvoiceProductVariants
              }
            />
          )}
          {tabIndex === 1 && (
            <ExpensesSection
              purchaseInvoiceExpenses={purchaseInvoice.purchaseInvoiceExpenses}
            />
          )}
          {tabIndex === 2 && (
            <DownPaymentAllocationsSection
              purchaseDownPaymentAllocations={
                purchaseInvoice.purchaseDownPaymentAllocations
              }
            />
          )}
        </Paper>

        {/* PDF Dialog */}
        <Dialog
          open={pdfDialogOpen}
          onClose={() => setPdfDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          slotProps={{
            paper: {
              sx: { height: "90vh" },
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Faktur Pembelian - {referenceNumber}
            <IconButton onClick={() => setPdfDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: "100%" }}>
            {purchaseInvoice && user && (
              <PDFViewer width="100%" height="100%">
                <PurchaseInvoicePdf
                  purchaseInvoice={{
                    ...purchaseInvoice,
                    purchaseInvoiceProductVariants: [
                      ...purchaseInvoice.purchaseInvoiceProductVariants,
                    ].sort((a, b) => a.name.localeCompare(b.name)),
                  }}
                  user={user}
                />
              </PDFViewer>
            )}
          </DialogContent>
        </Dialog>

        {/* Journal Details Dialog */}
        <JournalDetailsDialog
          open={journalDialogOpen}
          onClose={() => setJournalDialogOpen(false)}
          transactionableType="PURCHASE_INVOICE"
          transactionableId={purchaseInvoice?.id}
        />
      </Stack>
    </PageContainer>
  );
};
